/**
 * The cluster the admin reads, which is one cookie (`manifests-draw-the-nav` D9, D10).
 *
 * `__country` is the staff member's choice, shared with Flutter
 * (`admin-app/lib/src/cluster/cluster_cookie.dart`, `lib/main.dart`). The cookie alone does
 * not route this app's reads: the graph client sends no cookies cross-origin, and the edge
 * (MathGaps/jwt-worker `redirection.ts`) routes a Better Auth token by its `country` claim
 * before it reads the cookie. So the graph client names the cluster in the path instead —
 * `/au` or `/us`, the edge's highest-priority override (`clusterEndpoint`).
 */
export const CLUSTER_COOKIE = '__country';

export type Cluster = 'au' | 'us';

export const CLUSTERS = ['au', 'us'] as const satisfies readonly Cluster[];

/** Where neither the cookie nor the session names a cluster. */
const FALLBACK: Cluster = 'au';

/** A cookie that could not be read at all: the picker says so rather than naming a cluster. */
export const UNREADABLE = 'unreadable';

const A_YEAR = 60 * 60 * 24 * 365;

/** Registrable domains of two labels that are public suffixes, not sites. */
const PUBLIC_SUFFIXES = new Set([
  'com.au',
  'co.nz',
  'co.uk',
  'net.au',
  'org.au',
]);

/** `au` or `us` from any case, or `null` for anything else. */
export function clusterOf(value: string | null | undefined): Cluster | null {
  const named = value?.trim().toLowerCase();
  return CLUSTERS.find((cluster) => cluster === named) ?? null;
}

/**
 * The clusters the picker offers: both, in every build.
 *
 * D10 had `US` in production alone, *"as Flutter has it"*. Flutter has no such gate —
 * `avatar_menu.dart:354` is `for (final country in const [aus, us])`, unconditional — and
 * every environment has a US host to route to: the edge Worker's `[env.dev.vars]` names
 * `US_HOST = "us.tutero.dev"` beside `AU_HOST`, as staging and production name theirs
 * (`infrastructure/jwt-worker/wrangler.toml`). The premise was wrong, so the gate goes.
 *
 * It was not only hiding `US`. `offerable()` rewrote a `us` cookie to `au`, and the cookie
 * is shared across `*.tutero.dev` — so opening this admin moved a staff member's region
 * back to AU for Flutter and the teacher's app too.
 */
export function offerableClusters(): readonly Cluster[] {
  return CLUSTERS;
}

/**
 * The cookie's domain: the served host's registrable domain, so `admin.tutero.dev` and
 * `api-ts.tutero.dev` read one value. `null` where the host can carry no domain
 * attribute — the rig's `127.0.0.1`, where the cookie is the host's own.
 */
export function clusterCookieDomain(
  host: string = window.location.hostname,
): string | null {
  if (host === 'localhost' || host.endsWith('.localhost')) return 'localhost';
  if (/^\d+(\.\d+)*$/.test(host) || host.includes(':')) return null;
  const labels = host.split('.');
  if (labels.length <= 2) return host;
  const tail = labels.slice(-2).join('.');
  return labels.slice(PUBLIC_SUFFIXES.has(tail) ? -3 : -2).join('.');
}

/** The cookie's cluster, `null` where it names none, `UNREADABLE` where it cannot be read. */
export function readCluster(): Cluster | null | typeof UNREADABLE {
  let jar: string;
  try {
    jar = document.cookie;
  } catch {
    return UNREADABLE;
  }
  const named = jar
    .split(';')
    .map((pair) => pair.trim())
    .find((pair) => pair.startsWith(`${CLUSTER_COOKIE}=`))
    ?.slice(CLUSTER_COOKIE.length + 1);
  return clusterOf(named);
}

/** The cluster, lower-case, on the registrable domain (D9). */
export function writeCluster(cluster: Cluster): void {
  const domain = clusterCookieDomain();
  document.cookie = [
    `${CLUSTER_COOKIE}=${cluster.toLowerCase()}`,
    'path=/',
    ...(domain === null ? [] : [`domain=${domain}`]),
    `max-age=${A_YEAR}`,
    'samesite=lax',
    ...(window.location.protocol === 'https:' ? ['secure'] : []),
  ].join('; ');
}

/**
 * The cluster this tab is on, and what the cookie must be told — `write: null` where it
 * already agrees, so a reload of a chosen cluster writes nothing (D9).
 *
 * An absent cookie takes the session's `country` claim, which the auth Worker already
 * issues (`services/auth/src/auth.ts`); `au` is the fallback where neither can be read.
 */
export function settleCluster(
  cookie: Cluster | null | typeof UNREADABLE,
  claim?: string | null,
): { cluster: Cluster | typeof UNREADABLE; write: Cluster | null } {
  if (cookie === UNREADABLE) return { cluster: UNREADABLE, write: null };
  if (cookie !== null) return { cluster: cookie, write: null };
  const cluster = clusterOf(claim) ?? FALLBACK;
  return { cluster, write: cluster };
}

/** `settleCluster` against the live cookie, writing what it settles on. */
export function ensureCluster(
  claim?: string | null,
): Cluster | typeof UNREADABLE {
  const { cluster, write } = settleCluster(readCluster(), claim);
  if (write !== null) writeCluster(write);
  return cluster;
}

/**
 * The graph endpoint for a cluster: `base` with `/au` or `/us` appended, which the edge strips
 * and routes on before anything else — before the token's `country` claim and before an
 * impersonated account's (jwt-worker `getCorrectCountryOrigin`; the schools rig's edge honours
 * it too, `qa/src/edge/http.ts`). With no cluster, `base` alone, and the edge decides.
 */
export function clusterEndpoint(base: string, cluster: Cluster | null): string {
  if (cluster === null) return base;
  return `${base.replace(/\/+$/, '')}/${cluster}`;
}

/** The address a cluster reload keeps, so *View as*'s `?emulatedTeacher=` survives (D11). */
export function here(): string {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
}

/** The one navigation this module makes, in a box a test can watch. */
export const navigation = {
  reload(): void {
    window.location.assign(here());
  },
};

/** The cluster the cookie names right now; `null` for none, and for one it cannot read. */
export function currentCluster(): Cluster | null {
  const seen = readCluster();
  return seen === UNREADABLE || seen === null ? null : seen;
}

export interface ClusterWatch {
  /**
   * The cluster this tab loaded on. By default the cookie's now, and `null` where it
   * names none — then the first value seen is taken as this tab's own, which is how the
   * picker's own first write does not reload the tab that made it.
   */
  booted?: Cluster | null;
  /** True while the shell draws sign-in, not-staff or refusal: take the value, do not reload. */
  quiet: () => boolean;
  intervalMs?: number;
}

/**
 * Every open tab follows the cookie (D11, Flutter's `CookiePoller`): on a value this tab
 * did not load on, it reloads on the new cluster. Flutter skips `/login`, `/loading` and
 * `/admin-access`; here those are states the shell draws rather than paths, so `quiet`
 * is the condition — the new value is taken as this tab's own and nothing reloads.
 */
export function watchCluster({
  booted = currentCluster(),
  quiet,
  intervalMs = 2_000,
}: ClusterWatch): () => void {
  let held = booted;
  const timer = setInterval(() => {
    const now = currentCluster();
    if (now === null || now === held) return;
    const first = held === null;
    held = now;
    if (first || quiet()) return;
    navigation.reload();
  }, intervalMs);
  return () => clearInterval(timer);
}
